import { useAuth } from "@/features/auth";
import { queryClient, useApiClient } from "@/infrastructure/rest";
import { OngewensteGebeurtenisEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { useRemoveCacheListItem, useUpdateCacheListItem, useUpsertCacheListItem } from "@/infrastructure/tanstack-query/hooks";
import { OngewensteGebeurtenisDto } from "@/shared/types";
import { useMutation, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useCallback } from "react";
import { OngewensteGebeurtenis } from "./domain.ts";

const OG_QUERY_KEY = "ongewenste-gebeurtenissen";

type GetOngewensteGebeurtenissenOptions = Pick<UseQueryOptions<OngewensteGebeurtenis[]>, "select"> & {
    forceRefetch?: boolean;
};

/**
 * Use select if you want to filter the query result
 * Use forceRefetch if we want to ALWAYS refetch on mount
 */
export function useGetOngewensteGebeurtenissen(options: GetOngewensteGebeurtenissenOptions) {
    const { select, forceRefetch = false } = options;
    const apiClient = useApiClient();
    const { apiDomain } = useAuth();

    return useQuery({
        queryKey: [OG_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<OngewensteGebeurtenisDto[]>("/ongewenste-gebeurtenissen");
            return data?.map((dto) => OngewensteGebeurtenis.fromDto(dto)) ?? [].sort(OngewensteGebeurtenis.sort);
        },
        // Skip the query while apiDomain is still null (admin between mount and preview-role pick).
        // Otherwise the request goes to /api/ongewenste-gebeurtenissen without a role-prefix and 404s.
        enabled: apiDomain !== null,
        // When forceRefetch is set to true, we always re-run the query.
        // This is required when creating a non-relevant OG, where we explicitly want fresh data.
        // In all other cases, we still want to refetch on mount, but only if the data is stale.
        // Setting it to false would prevent refetching on mount entirely, which is not what we want.
        refetchOnMount: forceRefetch ? "always" : true,
        select,
    });
}

export function useGetRelevanteOngewensteGebeurtenissen() {
    return useGetOngewensteGebeurtenissen({
        select: (ogs) => {
            return ogs.filter((og) => og.relevant && og.status === "Nieuw").sort(OngewensteGebeurtenis.sort);
        },
    });
}

export function useDeclineOngewensteGebeurtenis() {
    const apiClient = useApiClient();

    return useMutation({
        mutationFn: async (spoorwebDossierNummer: string) => {
            await apiClient.patch(`/ongewenste-gebeurtenissen/${spoorwebDossierNummer}/status`, { status: "Geweigerd" });
        },
        onSuccess: async () => {
            // Invalidate the dossiers query to refetch after creating a new dossier
            await queryClient.invalidateQueries({ queryKey: [OG_QUERY_KEY] });
        },
        onError: (error) => {
            console.error("Error creating dossier:", error);
        },
    });
}

export function useRealtimeOngewensteGebeurtenissen() {
    const { create, update } = useUpdateOngewensteGebeurtenisQueriesData();

    useRealtimeTopic<OngewensteGebeurtenisEvent>("ongewensteGebeurtenis", {
        onMessage: (message) => {
            const { ongewensteGebeurtenis } = message;
            switch (message.reason) {
                case "Wijziging": {
                    return update(ongewensteGebeurtenis.spoorwebNummer, () => OngewensteGebeurtenis.fromDto(ongewensteGebeurtenis, true));
                }
                case "Nieuw":
                    return create(() => OngewensteGebeurtenis.fromDto(ongewensteGebeurtenis, true));
                default:
                    console.warn("Unsupported ongewenste gebeurtenis notification type:", message.reason);
                    return;
            }
        },
    });
}

type CreateOngewensteGebeurtenis = () => OngewensteGebeurtenis;
type UpdateOngewensteGebeurtenis = (og: OngewensteGebeurtenis) => OngewensteGebeurtenis;

export function useUpdateOngewensteGebeurtenisQueriesData() {
    const upsertCacheListItem = useUpsertCacheListItem();
    const updateCacheListItem = useUpdateCacheListItem();
    const removeCacheListItem = useRemoveCacheListItem();

    const update = useCallback(
        (spoorwebNummer: string, updateFn: UpdateOngewensteGebeurtenis) => {
            updateCacheListItem<OngewensteGebeurtenis, "nummer">({
                queryKey: [OG_QUERY_KEY],
                idField: "nummer",
                idToUpdate: spoorwebNummer,
                updateFn,
            });
        },
        [updateCacheListItem]
    );

    const create = useCallback(
        (createFn: CreateOngewensteGebeurtenis) => {
            upsertCacheListItem({
                queryKey: [OG_QUERY_KEY],
                idField: "nummer",
                createFn,
                sortFn: (items) => items.sort(OngewensteGebeurtenis.sort),
            });
        },
        [upsertCacheListItem]
    );

    const remove = useCallback(
        (idToDelete: string) => {
            removeCacheListItem<OngewensteGebeurtenis, "nummer">({
                queryKey: [OG_QUERY_KEY],
                idField: "nummer",
                idToDelete,
                sortFn: (items) => items.sort(OngewensteGebeurtenis.sort),
            });
        },

        [removeCacheListItem]
    );

    return { create, update, remove };
}
